'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOntwerp } from '@/contexts/OntwerpContext';
import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import {
  ArrowRight,
  Award,
  Check,
  Clock,
  Cloud,
  Code,
  Cpu,
  Database,
  Globe,
  HardDrive,
  Heart,
  Lock,
  type LucideIcon,
  Mail,
  Monitor,
  Music,
  Play,
  Radio,
  RefreshCw,
  Server,
  Settings,
  Shield,
  Smartphone,
  Star,
  TrendingUp,
  Users,
  Video,
  Waves,
  Wifi,
  Zap,
} from 'lucide-react';
import Image from 'next/image';
import type React from 'react';
import { BrandStripe } from './brand-stripe';
import { HeroDecoration } from './hero-decoration';

// Icon registry for string-based icon names (supports Server Component usage)
const iconRegistry: Record<string, LucideIcon> = {
  ArrowRight,
  Check,
  Play,
  Server,
  Globe,
  Shield,
  Lock,
  Zap,
  Database,
  Cpu,
  HardDrive,
  Cloud,
  Wifi,
  Radio,
  Music,
  Video,
  Monitor,
  Smartphone,
  Mail,
  Users,
  Settings,
  Star,
  Heart,
  Clock,
  RefreshCw,
  Award,
  TrendingUp,
  Code,
  // Streambewaking. Een naam die hier niet in staat wordt stil overgeslagen
  // (zie resolveIcon) — het badge-icoon verdwijnt dan zonder foutmelding.
  Waves,
};

export interface HeroAction {
  label: string;
  href: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  icon?: LucideIcon;
  iconName?: string; // String icon name for Server Component usage
  /** Alleen tonen in design system v1 (bv. een knop die de nieuwe plaat toevoegt). */
  alleenV1?: boolean;
}

export interface HeroBadge {
  text: string;
  icon?: LucideIcon;
  iconName?: string; // String icon name for Server Component usage
  variant?: 'default' | 'secondary' | 'outline' | 'destructive';
}

export type HeroVariant = 'centered' | 'split' | 'minimal' | 'fullscreen' | 'product' | 'video';
export type HeroBackground =
  | 'default'
  | 'white'
  | 'gray'
  | 'dark'
  | 'navy'
  | 'gradient'
  | 'primary'
  /** Zachte merk-tint met donkere tekst — open, rustige opening. */
  | 'blush';

export interface HeroProps {
  /**
   * Layout variant
   * - 'centered': Classic center-aligned hero
   * - 'split': Image + content side by side
   * - 'minimal': Simple text-only hero
   * - 'fullscreen': Full viewport height
   * - 'product': Product-focused with feature list
   * - 'video': Background video hero
   */
  variant?: HeroVariant;

  /**
   * Optional badge shown above the title
   */
  badge?: HeroBadge;

  /**
   * Main heading text
   */
  title: string | React.ReactNode;

  /**
   * Subtitle/description text
   */
  subtitle?: string | React.ReactNode;

  /**
   * Call-to-action buttons
   */
  actions?: HeroAction[];

  /**
   * Image source for split/product variants
   */
  image?: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };

  /**
   * Custom visual component for split/product variants (overrides image)
   */
  visualSlot?: React.ReactNode;
  /**
   * Design system v1: scène over de volle breedte (licht en donker), zoals de
   * voorpagina. Rechtsonder vastgezet; de tekst staat links op een verloop.
   * Gaat voor `visualSlot`. Alleen in v1.
   */
  scene?: { licht: string; donker: string; alt?: string };
  /** Kaart die in de scène zweeft, tussen tekst en mascotte (plaat radio). */
  sceneKaart?: React.ReactNode;

  /**
   * Background image URL (for fullscreen/centered variants)
   */
  backgroundImage?: string;

  /**
   * Background video URL (for video variant)
   */
  backgroundVideo?: string;

  /**
   * Image position for split variant
   */
  imagePosition?: 'left' | 'right';

  /**
   * Background style
   */
  background?: HeroBackground;

  /**
   * Feature list (for product variant)
   */
  features?: string[];

  /**
   * Trust indicators section
   */
  trustIndicators?: React.ReactNode;

  /**
   * Size (vertical padding)
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Custom content (for advanced use cases like WHOIS checks, custom forms, etc.)
   * When provided, this replaces the default title/subtitle/actions
   */
  children?: React.ReactNode;

  /**
   * Additional CSS classes for the section
   */
  className?: string;

  /**
   * Show decorative gradient background
   */
  showGradient?: boolean;

  /**
   * Show decorative glow behind image (split variant only)
   */
  showImageGlow?: boolean;

  /**
   * Show decorative dot pattern on both content side (left) and visual side (right)
   * Only applies to split variant
   */
  showDotPattern?: boolean;

  /**
   * Show SVG decoration overlay (subtle brand decoration)
   */
  showDecoration?: boolean;

  /**
   * Decoration opacity (0-100)
   * @default 12
   */
  decorationOpacity?: number;
}

const backgroundStyles: Record<HeroBackground, string> = {
  default: 'bg-white dark:bg-gray-950',
  white: 'bg-white dark:bg-gray-900',
  gray: 'bg-gray-50 dark:bg-gray-950',
  dark: 'bg-gray-900 dark:bg-black',
  navy: 'bg-navy',
  gradient: 'bg-gray-50 dark:bg-gray-950',
  primary: 'bg-primary-600 dark:bg-primary-700',
  blush:
    'bg-gradient-to-b from-[#FFEDE3] via-[#FDF3EE] to-[#FFF4F8] dark:from-gray-900 dark:via-gray-950 dark:to-gray-950',
};

const sizeStyles = {
  sm: 'py-8 sm:py-12',
  md: 'py-12 sm:py-16',
  lg: 'py-16 sm:py-20',
  xl: 'py-20 sm:py-24',
};

export function Hero({
  variant = 'centered',
  badge,
  title,
  subtitle,
  actions = [],
  image,
  visualSlot,
  scene,
  sceneKaart,
  backgroundImage,
  backgroundVideo,
  imagePosition = 'right',
  background = 'default',
  features,
  trustIndicators,
  size = 'lg',
  children,
  className,
  showGradient = false,
  showImageGlow = false,
  showDotPattern = false,
  showDecoration = false,
  decorationOpacity = 12,
}: HeroProps) {
  // Design system v1: geen donkere hero's meer; alles op de lichte tint, zoals
  // de voorpagina. De pagina's geven nog 'navy' mee — dat wordt hier vertaald.
  const v1 = useOntwerp() === 'v1';
  const zichtbareActies = actions.filter((a) => v1 || !a.alleenV1);
  // Met een scène staat de tekst altijd links naast het beeld, ook als de pagina
  // 'centered' meegeeft voor de huidige weergave.
  const gecentreerdeVariant = variant === 'centered' && !(v1 && scene);
  const isDark =
    !v1 && (background === 'dark' || background === 'navy' || background === 'primary');
  const achtergrond = v1 ? 'bg-tint dark:bg-gray-950' : backgroundStyles[background];

  // Helper to resolve icon from either icon prop or iconName string
  const resolveIcon = (icon?: LucideIcon, iconName?: string): LucideIcon | undefined => {
    if (icon) return icon;
    if (iconName && iconRegistry[iconName]) return iconRegistry[iconName];
    return undefined;
  };

  // Render badge if provided
  const renderBadge = () => {
    if (!badge) return null;

    const BadgeIcon = resolveIcon(badge.icon, badge.iconName);

    return (
      <Badge
        className={cn('mb-6', isDark && 'bg-white/20 text-white border-white/30')}
        variant={badge.variant || 'secondary'}
      >
        {BadgeIcon && <BadgeIcon className="mr-2 h-4 w-4" />}
        {badge.text}
      </Badge>
    );
  };

  // Render features list - clean dot bullets (matches pricing card style)
  const renderFeatures = () => {
    if (!features || features.length === 0) return null;

    const isCentered = gecentreerdeVariant || variant === 'fullscreen';

    return (
      <div
        className={cn(
          'grid grid-cols-2 gap-x-8 gap-y-2.5',
          isCentered ? 'max-w-lg mx-auto' : 'max-w-md'
        )}
      >
        {features.map((feature, index) => (
          <div
            key={index}
            className={cn(
              'flex items-center gap-2.5',
              isDark ? 'text-gray-200' : 'text-gray-600 dark:text-gray-400'
            )}
          >
            {v1 ? (
              // v1 (plaat webhosting): vinkje in een blauwe cirkel
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blauw-600 text-white dark:bg-blauw-500">
                <Check aria-hidden="true" className="h-3 w-3" strokeWidth={3} />
              </span>
            ) : (
              <Check
                className={cn(
                  'h-4 w-4 flex-shrink-0',
                  isDark ? 'text-primary-400' : 'text-primary-600 dark:text-primary-400'
                )}
                strokeWidth={2.5}
              />
            )}
            <span className={cn('text-sm font-medium', v1 && 'text-gray-800 dark:text-gray-200')}>
              {feature}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Render actions/CTAs
  const renderActions = () => {
    if (zichtbareActies.length === 0) return null;

    const isCenter = gecentreerdeVariant || variant === 'fullscreen' || variant === 'video';

    return (
      <div
        className={cn(
          'flex flex-col items-stretch gap-3 sm:flex-row sm:items-center',
          isCenter ? 'justify-center' : 'justify-start'
        )}
      >
        {zichtbareActies.map((action, index) =>
          // Eerste actie = primaire CTA met de ronde-pijl-signatuur (pink pill).
          index === 0 ? (
            <Link
              key={index}
              href={action.href}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-700"
            >
              {action.label}
              <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            // Secundaire actie = outline-pill.
            <Link
              key={index}
              href={action.href}
              className={cn(
                'inline-flex items-center justify-center rounded-full border px-6 py-3 text-base font-semibold transition-colors',
                isDark
                  ? 'border-white/40 text-white hover:bg-white/10'
                  : 'border-gray-300 text-gray-900 hover:border-gray-900 dark:border-gray-700 dark:text-white',
                v1 &&
                  'border-blauw-300 bg-white hover:border-blauw-500 dark:border-gray-700 dark:bg-gray-900'
              )}
            >
              {action.label}
            </Link>
          )
        )}
      </div>
    );
  };

  // Render default content (title, subtitle, actions)
  const isCentered = gecentreerdeVariant || variant === 'fullscreen' || variant === 'video';

  const renderDefaultContent = () => (
    <div className={cn('space-y-6', isCentered && 'text-center')}>
      {/* Badge */}
      <div>{renderBadge()}</div>

      {/* Title
          De split-variant rendert zowel deze standaardinhoud als `children`.
          Een pagina die zijn kop in `children` zet — de voorpagina doet dat —
          geeft daarom `title=""` mee, en dat leverde een lége <h1> op vóór de
          echte: twee koppen op de pagina, waarvan de eerste zonder tekst.
          Vandaar deze controle in plaats van altijd renderen. */}
      {title ? (
        <div>
          {typeof title === 'string' ? (
            <h1
              className={cn(
                'hero-title',
                isDark ? 'text-white' : 'text-gray-900 dark:text-white',
                isCentered && 'max-w-4xl mx-auto'
              )}
            >
              {title}
            </h1>
          ) : (
            <div className={cn(isCentered ? 'max-w-4xl mx-auto' : '')}>{title}</div>
          )}
        </div>
      ) : null}

      {/* Subtitle */}
      {subtitle && (
        <div>
          {typeof subtitle === 'string' ? (
            <p
              className={cn(
                'text-base sm:text-lg md:text-xl leading-relaxed',
                isDark ? 'text-gray-300' : 'text-gray-600 dark:text-gray-400',
                isCentered ? 'max-w-2xl mx-auto' : 'max-w-xl'
              )}
            >
              {subtitle}
            </p>
          ) : (
            <div
              className={cn(
                isCentered ? 'max-w-2xl mx-auto' : 'max-w-xl',
                v1 && '[&_*]:text-gray-600 dark:[&_*]:text-gray-300'
              )}
            >
              {subtitle}
            </div>
          )}
        </div>
      )}

      {/* Features - with separator */}
      {features && features.length > 0 && <div className="pt-2">{renderFeatures()}</div>}

      {/* Actions */}
      {zichtbareActies.length > 0 && <div className="pt-2">{renderActions()}</div>}

      {/* Trust Indicators */}
      {trustIndicators && (
        <div
          className={cn(
            'pt-8 border-t',
            isDark ? 'border-white/10' : 'border-gray-200 dark:border-gray-800'
          )}
        >
          {trustIndicators}
        </div>
      )}
    </div>
  );

  // A) CENTERED VARIANT (Classic) — in v1 gaat een scène voor
  if (gecentreerdeVariant) {
    return (
      <section
        className={cn('relative z-10 overflow-hidden', achtergrond, sizeStyles[size], className)}
      >
        {showDecoration && <HeroDecoration opacity={decorationOpacity} position="right" />}
        {backgroundImage && (
          <div className="absolute inset-0 z-0">
            <Image src={backgroundImage} alt="" fill className="object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50" />
          </div>
        )}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">{children || renderDefaultContent()}</div>
        </div>
      </section>
    );
  }

  // B0) SCÈNE (alleen design system v1)
  if (v1 && scene) {
    return (
      <section
        className={cn(
          'relative isolate overflow-hidden bg-tint text-gray-900 dark:bg-gray-950 dark:text-white',
          className
        )}
      >
        {/* Desktop: scène rechtsonder vast, verloop van links voor de tekst. De
            donkere versie is lazy, zodat het lichte thema hem niet ophaalt. */}
        {/* top-6: de scène begint onder de zwevende header, niet erachter */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 top-6 hidden lg:block">
          <Image
            src={scene.licht}
            alt={scene.alt ?? ''}
            fill
            priority
            sizes="100vw"
            quality={90}
            className={cn('dark:hidden', 'object-contain object-right-bottom')}
          />
          <Image
            src={scene.donker}
            alt={scene.alt ?? ''}
            fill
            loading="lazy"
            sizes="100vw"
            quality={90}
            className={cn('hidden dark:block', 'object-contain object-right-bottom')}
          />
          <div className="absolute inset-y-0 left-0 w-[58%] bg-gradient-to-r from-tint/95 via-tint/70 to-transparent dark:from-gray-950/90 dark:via-gray-950/60" />
          {/* Ook boven, rechts en onder zacht laten overlopen, zodat de scène
              geen geplakt plaatje met harde randen wordt (Kobus 05-10). */}
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-tint to-transparent dark:from-gray-950" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-tint via-tint/70 to-transparent dark:from-gray-950 dark:via-gray-950/70" />
          <div className="absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-tint via-tint/60 to-transparent dark:from-gray-950 dark:via-gray-950/60" />
        </div>

        {sceneKaart && (
          <div className="pointer-events-none absolute left-[40.5%] top-[11%] z-10 hidden w-[min(17rem,19vw)] xl:block">
            {sceneKaart}
          </div>
        )}

        <div className="container relative z-10 mx-auto px-4 pb-8 pt-10 sm:pt-14 lg:flex lg:min-h-[clamp(620px,40vw,700px)] lg:items-center lg:pb-24">
          <div className="max-w-xl lg:max-w-[min(46vw,640px)]">
            {renderDefaultContent()}
            {children}
          </div>
        </div>

        {/* Mobiel: de scène onder de tekst, bijgesneden op de mascotte. */}
        <div className="relative aspect-[5/4] w-full sm:aspect-[16/10] lg:hidden">
          <Image
            src={scene.licht}
            alt=""
            fill
            sizes="100vw"
            quality={90}
            className="object-cover object-[78%_70%] dark:hidden"
          />
          <Image
            src={scene.donker}
            alt=""
            fill
            loading="lazy"
            sizes="100vw"
            quality={90}
            className="hidden object-cover object-[78%_70%] dark:block"
          />
        </div>
      </section>
    );
  }

  // B) SPLIT VARIANT
  if (variant === 'split') {
    return (
      <>
        <section
          className={cn('relative z-10 overflow-hidden', achtergrond, sizeStyles[size], className)}
        >
          {showDecoration && (
            <HeroDecoration
              opacity={decorationOpacity}
              position={imagePosition === 'left' ? 'left' : 'right'}
              mirror={imagePosition === 'left'}
            />
          )}
          <div className="container mx-auto px-4 relative z-0">
            <div
              className={cn(
                'grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto',
                imagePosition === 'left' &&
                  'lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1'
              )}
            >
              <div className="relative">
                {/* Decorative Dot Pattern - Top Left of content (light pink, behind content) */}
                {showDotPattern && (
                  <div className="absolute left-0 -top-4 md:-top-6 w-24 h-28 md:w-32 md:h-36 z-0 pointer-events-none">
                    <div className="grid grid-cols-6 gap-2.5 md:gap-3 opacity-60">
                      {Array.from({ length: 36 }).map((_, i) => (
                        <div
                          key={`dot-tl-${i}`}
                          className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-primary-200 dark:bg-primary-400/40"
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className="relative z-10">
                  {renderDefaultContent()}
                  {children}
                </div>
              </div>
              <div className="relative">
                {/* Decorative Dot Pattern - Visual side (Top Right of visual) */}
                {showDotPattern && (
                  <div className="absolute right-0 top-0 md:-right-2 md:-top-2 w-20 h-24 md:w-28 md:h-32 z-30 opacity-40 pointer-events-none">
                    <div className="grid grid-cols-6 gap-2 md:gap-2.5">
                      {Array.from({ length: 36 }).map((_, i) => (
                        <div
                          key={`dot-tr-${i}`}
                          className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-gray-300/70 dark:bg-gray-500/50"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {visualSlot ? (
                  <div className="relative w-full">{visualSlot}</div>
                ) : image ? (
                  <div className="relative w-full">
                    {/* Decorative glow behind image */}
                    {showImageGlow && (
                      <div className="absolute -inset-8 bg-gray-100/40 dark:bg-gray-800/30 rounded-xl blur-xl" />
                    )}
                    <Image
                      src={image.src}
                      alt={image.alt}
                      width={image.width || 600}
                      height={image.height || 600}
                      className="relative w-full h-auto object-contain rounded-xl drop-shadow-lg"
                      priority
                    />
                  </div>
                ) : (
                  <div className="relative aspect-[4/3] w-full max-w-md mx-auto lg:max-w-xl bg-primary-100 dark:bg-gray-800 rounded-xl shadow-lg" />
                )}
              </div>
            </div>
          </div>
        </section>
        {/* Buiten de sectie: binnenin zou de doorlopende mascotte er overheen tekenen. */}
        {!v1 && <BrandStripe />}
      </>
    );
  }

  // C) MINIMAL VARIANT
  if (variant === 'minimal') {
    return (
      <section className={cn('relative', achtergrond, sizeStyles[size], className)}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">{children || renderDefaultContent()}</div>
        </div>
      </section>
    );
  }

  // D) FULLSCREEN VARIANT
  if (variant === 'fullscreen') {
    return (
      <section
        className={cn(
          'relative min-h-[80vh] lg:min-h-screen flex items-center overflow-hidden',
          achtergrond,
          className
        )}
      >
        {backgroundImage && (
          <div className="absolute inset-0 z-0">
            <Image src={backgroundImage} alt="" fill className="object-cover" priority />
            <div className="absolute inset-0 bg-black/50" />
          </div>
        )}
        <div className="container mx-auto px-4 relative z-10 py-20">
          <div className="max-w-4xl mx-auto text-center">{children || renderDefaultContent()}</div>
        </div>
      </section>
    );
  }

  // E) PRODUCT VARIANT
  if (variant === 'product') {
    return (
      <section
        className={cn('relative z-10 overflow-hidden', achtergrond, sizeStyles[size], className)}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div
              className={cn(
                'grid lg:grid-cols-2 gap-12 lg:gap-16 items-center',
                imagePosition === 'left' &&
                  'lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1'
              )}
            >
              <div>{children || renderDefaultContent()}</div>
              <div className="relative">
                {image ? (
                  <Image
                    src={image.src}
                    alt={image.alt}
                    width={image.width || 600}
                    height={image.height || 450}
                    className="w-full h-auto rounded-xl shadow-lg"
                    priority
                  />
                ) : (
                  <div className="aspect-[4/3] w-full bg-primary-100 dark:bg-gray-800 rounded-xl shadow-lg" />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // F) VIDEO VARIANT
  if (variant === 'video') {
    return (
      <section
        className={cn(
          'relative min-h-[80vh] lg:min-h-screen flex items-center overflow-hidden',
          className
        )}
      >
        {backgroundVideo && (
          <div className="absolute inset-0 z-0">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={backgroundVideo} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/60" />
          </div>
        )}
        {!backgroundVideo && backgroundImage && (
          <div className="absolute inset-0 z-0">
            <Image src={backgroundImage} alt="" fill className="object-cover" priority />
            <div className="absolute inset-0 bg-black/60" />
          </div>
        )}
        <div className="container mx-auto px-4 relative z-10 py-20">
          <div className="max-w-4xl mx-auto text-center">{children || renderDefaultContent()}</div>
        </div>
      </section>
    );
  }

  return null;
}

// ============================================================================
// Preset Hero Components (convenience wrappers)
// ============================================================================

/**
 * B) SplitHero - Classic split layout with image
 */
export function SplitHero(props: Omit<HeroProps, 'variant'>) {
  return <Hero variant="split" {...props} />;
}
